/**
 * The target-field catalogue for the student import.
 *
 * Every entry here is a field the wizard can write into the API. The
 * `aliases` are the header spellings actually observed in the real
 * registration spreadsheet (تسجيل 2026 كامل.xlsx — 11 grade sheets,
 * 691 rows, 31 columns) plus the obvious variants the office types.
 *
 * Matching is deterministic: aliases are normalized (see normalizeHeader
 * in importLogic.js) and compared for equality. There is no fuzzy
 * scoring, no AI, no network call — a header either matches an alias or
 * it is left for the user to map by hand in step 2.
 *
 * ── The duplicate-header problem ──────────────────────────────────────
 * The real file carries "اسم الطالب" TWICE:
 *   column C  = the composed full name  ("محمود حسين محمد")
 *   column N  = the first name only     ("محمود")   — header is
 *               "اسم الطالب " with a trailing space, which normalizes
 *               to exactly the same string as C.
 * Normalized text alone therefore cannot tell them apart. Each target
 * carries `preferIndex` — the 0-based column position it sits at in the
 * real file — and the matcher uses that position to break exactly this
 * kind of tie. See resolveHeaderConflicts in importLogic.js.
 */

/** Where a mapped value is ultimately written. */
export const TARGET_SCOPE = {
  STUDENT: 'student',   // → students table (POST /students then PATCH /students/:id)
  FAMILY: 'family',     // → families table (POST /families, deduped across siblings)
  CONTEXT: 'context',   // → not persisted directly; drives placement/reporting
};

/**
 * Value kinds, which decide the normalizer + validator applied in step 3.
 *   text   — trimmed, whitespace-collapsed Arabic/Latin text
 *   phone  — digits + optional leading '+', tolerant of Arabic labels
 *   date   — 'd.m.yyyy', 'd/m/yyyy' or an Excel serial → 'YYYY-MM-DD'
 *   nid    — national id: digits only, expected 11, warn otherwise
 *   gender — ذكر/انثى → male/female
 *   number — integer-ish
 */
export const KIND = {
  TEXT: 'text',
  PHONE: 'phone',
  DATE: 'date',
  NID: 'nid',
  GENDER: 'gender',
  NUMBER: 'number',
};

/**
 * The full catalogue, in the column order of the real file so the
 * mapping screen reads top-to-bottom like the spreadsheet does.
 *
 *   key         internal id, used as the parsed row's property name
 *   label       what the office sees in the mapping UI
 *   column      target DB column (null for CONTEXT fields)
 *   scope       student | family | context
 *   kind        value kind, drives normalize+validate
 *   required    a row without it cannot be imported at all
 *   preferIndex the 0-based position this field occupies in the real
 *               sheet; used ONLY to break alias ties between two
 *               headers that normalize identically
 *   aliases     accepted header spellings (normalized before compare)
 */
export const TARGET_FIELDS = [
  {
    key: 'serial',
    label: 'التسلسل',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.NUMBER,
    required: false,
    preferIndex: 0,
    aliases: ['م', 'رقم', 'تسلسل', 'التسلسل', 'م.'],
  },
  {
    key: 'registration_number',
    label: 'الرقم المدرسي',
    column: 'registration_number',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 1,
    aliases: ['الرقم المدرسي', 'رقم مدرسي', 'الرقم الاكاديمي', 'رقم الطالب', 'رقم السجل العام'],
  },
  {
    // Column C — the composed full name. This is students.name, the one
    // stored full name the whole app reads (see 0006 migration note).
    key: 'name',
    label: 'اسم الطالب (الاسم الكامل)',
    column: 'name',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: true,
    preferIndex: 2,
    aliases: ['اسم الطالب', 'الاسم الكامل', 'اسم الطالب الكامل', 'الاسم', 'اسم التلميذ'],
  },
  {
    key: 'grade_title',
    label: 'الصف',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 3,
    aliases: ['الصف', 'الصف الدراسي', 'صف'],
  },
  {
    key: 'section_title',
    label: 'الشعبة',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 4,
    aliases: ['الشعبة', 'شعبة', 'الشعبه'],
  },
  {
    // Only when the file has it as its own column; otherwise the period is
    // read from the end of the section cell («أولى م»).
    key: 'batch_title',
    label: 'الفوج',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.TEXT,
    required: false,
    aliases: ['الفوج', 'فوج', 'الدوام', 'الفترة'],
  },
  {
    key: 'bus_number',
    label: 'رقم السيارة',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 5,
    aliases: ['رقم السيارة', 'السيارة', 'الباص', 'رقم الباص', 'النقل'],
  },
  {
    key: 'gender',
    label: 'الجنس',
    column: 'gender',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.GENDER,
    required: false,
    preferIndex: 6,
    aliases: ['الجنس', 'جنس', 'النوع'],
  },
  {
    key: 'address',
    label: 'العنوان',
    column: 'address',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 7,
    aliases: ['العنوان بالتفصيل', 'العنوان', 'عنوان', 'السكن', 'مكان السكن'],
  },
  {
    key: 'father_phone',
    label: 'هاتف الأب',
    column: 'father_phone',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.PHONE,
    required: false,
    preferIndex: 8,
    aliases: ['هاتف الاب', 'هاتف الأب', 'رقم الاب', 'جوال الاب', 'موبايل الاب'],
  },
  {
    key: 'mother_phone',
    label: 'هاتف الأم',
    column: 'mother_phone',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.PHONE,
    required: false,
    preferIndex: 9,
    aliases: ['هاتف الام', 'هاتف الأم', 'رقم الام', 'جوال الام', 'موبايل الام'],
  },
  {
    key: 'student_phone',
    label: 'هاتف الطالب',
    column: 'student_phone',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.PHONE,
    required: false,
    preferIndex: 10,
    aliases: ['هاتف الطالب', 'رقم الطالب', 'جوال الطالب', 'موبايل الطالب'],
  },
  {
    // Header in the real file is "الرقم  الوطني" — TWO spaces. The
    // normalizer collapses runs of whitespace, so the single-space
    // alias below matches it.
    key: 'national_id',
    label: 'الرقم الوطني',
    column: 'national_id',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.NID,
    required: false,
    preferIndex: 11,
    aliases: ['الرقم الوطني', 'رقم وطني', 'الرقم القومي', 'رقم الهوية', 'الرقم الشخصي'],
  },
  {
    key: 'birth_date',
    label: 'تاريخ الولادة',
    column: 'birth_date',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.DATE,
    required: false,
    preferIndex: 12,
    aliases: ['تاريخ الولادة', 'تاريخ الميلاد', 'المواليد', 'الولادة', 'تاريخ الولاده'],
  },
  {
    // Column N — "اسم الطالب " (trailing space). Normalizes to the same
    // text as column C, so position is what separates them.
    key: 'first_name',
    label: 'الاسم الأول',
    column: 'first_name',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 13,
    aliases: ['الاسم الاول', 'الاسم الأول', 'اسم الطالب', 'الاسم المجرد'],
  },
  {
    // The surname lives on the FAMILY (migration 0006), not the student.
    key: 'last_name',
    label: 'الكنية',
    column: 'last_name',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 14,
    aliases: ['الكنية', 'الكنيه', 'كنية', 'النسبة', 'اللقب'],
  },
  {
    key: 'father_name',
    label: 'اسم الأب',
    column: 'father_name',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 15,
    aliases: ['الاب', 'الأب', 'اسم الاب', 'اسم الأب', 'والد'],
  },
  {
    key: 'mother_name',
    label: 'اسم الأم',
    column: 'mother_name',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 16,
    aliases: ['الام', 'الأم', 'اسم الام', 'اسم الأم', 'والدة'],
  },
  {
    key: 'mother_last_name',
    label: 'كنية الأم',
    column: 'mother_last_name',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 17,
    aliases: ['كنية الام', 'كنية الأم', 'كنيه الام', 'لقب الام'],
  },
  {
    key: 'grandfather_name',
    label: 'اسم الجد',
    column: 'grandfather_name',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 18,
    aliases: ['الجد', 'اسم الجد', 'جد'],
  },
  {
    key: 'birth_place',
    label: 'مكان الولادة',
    column: 'birth_place',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 19,
    aliases: ['مكان الولادة', 'مكان الميلاد', 'محل الولادة', 'مكان الولاده'],
  },
  {
    // Header in the real file is misspelled: "تاريخ التجسيل" (should be
    // التسجيل). Both spellings are aliases so the real file auto-matches.
    key: 'registration_date',
    label: 'تاريخ التسجيل',
    column: 'registration_date',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.DATE,
    required: false,
    preferIndex: 20,
    aliases: ['تاريخ التجسيل', 'تاريخ التسجيل', 'تاريخ القيد', 'تا التسجيل'],
  },
  {
    key: 'amana',
    label: 'الأمانة',
    column: 'amana',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 21,
    aliases: ['الامانة', 'الأمانة', 'امانة', 'الامانه'],
  },
  {
    key: 'registry_place',
    label: 'محل القيد',
    column: 'registry_place',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 22,
    aliases: ['محل القيد', 'مكان القيد', 'القيد'],
  },
  {
    key: 'registry_number',
    label: 'رقم القيد',
    column: 'registry_number',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 23,
    aliases: ['رقم القيد', 'القيد رقم'],
  },
  {
    key: 'displacement_status',
    label: 'وثائق ثبوتية دراسية',
    column: 'displacement_status',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 24,
    aliases: ['الجلاء', 'جلاء', 'الجلا', 'وثائق ثبوتية دراسية', 'وثائق دراسية'],
  },
  {
    key: 'photo_note',
    label: 'صورة شخصية',
    column: null,
    scope: TARGET_SCOPE.CONTEXT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 25,
    aliases: ['صور شخصية', 'صورة شخصية', 'الصورة', 'صورة'],
  },
  {
    key: 'id_document_type',
    label: 'وثائق ثبوتية',
    column: 'id_document_type',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 26,
    aliases: ['هوية', 'نوع الهوية', 'الهوية', 'هويه', 'وثائق ثبوتية', 'الوثائق الثبوتية'],
  },
  {
    key: 'origin_place',
    label: 'وافد من',
    column: 'origin_place',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 27,
    aliases: ['وافد من', 'قادم من', 'الوافد من', 'المدرسة السابقة'],
  },
  {
    // In the real file this column mixes a leave date and a reason in
    // one free-text cell. It is imported as a NOTE, never as a status
    // change — deleteStudent (which is what sets status/leave_reason)
    // requires a validated reason and is deliberately out of scope here.
    key: 'leave_note',
    label: 'تاريخ وسبب المغادرة',
    column: 'leave_note',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 28,
    aliases: ['تا مغادرة المدرسة و السبب', 'تاريخ مغادرة المدرسة و السبب', 'المغادرة', 'سبب المغادرة'],
  },
  {
    key: 'notes',
    label: 'ملاحظات',
    column: 'notes',
    scope: TARGET_SCOPE.STUDENT,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 29,
    aliases: ['ملاحظات', 'ملاحظة', 'الملاحظات', 'التعليق'],
  },
  {
    key: 'father_job',
    label: 'عمل الأب',
    column: 'father_job',
    scope: TARGET_SCOPE.FAMILY,
    kind: KIND.TEXT,
    required: false,
    preferIndex: 30,
    aliases: ['عمل الاب', 'عمل الأب', 'مهنة الاب', 'وظيفة الاب'],
  },
  { key: 'admin_app_note', label: 'متابعة تطبيق المدير (ملاحظة)', column: null, scope: TARGET_SCOPE.CONTEXT, kind: KIND.TEXT, aliases: ['تطبيق المدير'] },
  { key: 'year_result_note', label: 'نتيجة العام السابق (ملاحظة)', column: null, scope: TARGET_SCOPE.CONTEXT, kind: KIND.TEXT, aliases: ['نتيجة هذا العام', 'نتيجة العام السابق'] },
  { key: 'extra_note', label: 'ملاحظة إضافية', column: null, scope: TARGET_SCOPE.CONTEXT, kind: KIND.TEXT, aliases: ['ملاحظة إضافية'] },
];

/** Fast lookup by key. */
export const FIELD_BY_KEY = TARGET_FIELDS.reduce((acc, f) => {
  acc[f.key] = f;
  return acc;
}, {});

/** Keys that must be mapped before the wizard may leave step 2. */
export const REQUIRED_KEYS = TARGET_FIELDS.filter((f) => f.required).map((f) => f.key);

/** Fields written to `families`. */
export const FAMILY_KEYS = TARGET_FIELDS
  .filter((f) => f.scope === TARGET_SCOPE.FAMILY)
  .map((f) => f.key);

/**
 * Student fields that POST /students accepts directly. Everything else
 * on the student has to follow as a PATCH — createStudent's body is
 * only { name, family_id, student_phone, tuition_fee, transport_fee }.
 */
export const STUDENT_CREATE_KEYS = ['name', 'student_phone'];

/**
 * Student fields PATCH /students/:id accepts, mirroring
 * STUDENT_EDITABLE_FIELDS in api/src/routes/people.js. Anything not in
 * this list is dropped before the request rather than being sent and
 * silently ignored.
 */
export const STUDENT_PATCH_KEYS = [
  'name', 'first_name', 'last_name', 'grandfather_name', 'gender',
  'birth_date', 'birth_place', 'national_id', 'student_phone',
  'picture_url', 'registration_number', 'registration_date',
  'family_status', 'health_status', 'notes', 'tuition_fee', 'transport_fee',
  'registry_place', 'registry_number', 'displacement_status',
  'origin_place', 'id_document_type',
];

/**
 * Family fields PATCH /families/:id accepts (FAMILY_EDITABLE_FIELDS in
 * people.js). POST /families accepts all of these EXCEPT mother_last_name
 * and amana, which is why a created family may need a follow-up PATCH.
 */
export const FAMILY_CREATE_KEYS = [
  'father_name', 'father_phone', 'father_job',
  'mother_name', 'mother_phone', 'last_name', 'address', 'notes',
];

export const FAMILY_PATCH_KEYS = [
  ...FAMILY_CREATE_KEYS, 'mother_last_name', 'amana',
];
